"use client";

import ConfirmDeleteModal from "@/components/atoms/confirm-delete-modal";
import { Locales } from "@/lib/i18n";
import { __, _x } from "@/lib/i18n/functions";
import { cn, formatPrice, getMedusaProductPrice, isSaleProduct } from "@/lib/utils";
import { ProductDocument } from "@/prismicio-types";
import { Loader2, Trash2Icon } from "lucide-react";
import Image from "next/image";
import { StoreProduct } from "@medusajs/types";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { Button } from "@/components/atoms/buttons";
import { useCart } from "@/hooks/useCart";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import QuantitySelector from "@/components/atoms/quantity-selector";
import { useEffect, useState } from "react";
import CartProductCard from "@/components/organisms/cart-product-card";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { useWishlist } from "@/hooks/useWishlist";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import { Badge } from "@/components/ui/badge";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { useProductVariant } from "@/hooks/useProductVariant";
import {
  getCartQuantityStep,
  normalizeConditioning,
  normalizePieceQuantity,
  resolveProductConditioning,
  toLotPrice,
  toPiecePrice,
} from "@/lib/product-units";

type WishlistProductCardProps = {
  locale: Locales;
  product: ProductDocument;
  medusaProduct: StoreProduct;
  isRange: boolean;
  onQuantityChange?: (quantity: number) => void;
  initialQuantity: number | undefined;
};

const WishlistProductCard = ({
  locale,
  product,
  medusaProduct,
  isRange = false,
  onQuantityChange,
  initialQuantity,
}: WishlistProductCardProps) => {
  const { cart } = useCart();
  const productId = medusaProduct?.id;

  // Variant resolution — single source of truth
  // No URL seeding on wishlist — store is the sole authority here.
  const { hasMultipleVariants, selectedVariant, selectedVariantPrice, hasCustomization, resolvedSurface, setVariant } =
    useProductVariant(medusaProduct, productId);

  //  Seed store with default surface on first render
  // RangeDropdown only fires onChange when the user actively changes the value.
  // If the user never opens the configuration modal (or opens it and leaves the
  // dropdown at its default), the store stays empty → hasCustomization=false →
  // "Please customize" error when clicking Add to Cart.
  //
  // Fix: when this is a multi-variant range product with no stored surface yet,
  // write resolvedSurface (= surface_min at this point) to the store immediately
  // on mount. This makes every range product "configured" by default.
  useEffect(() => {
    if (hasMultipleVariants && !hasCustomization && resolvedSurface) {
      setVariant(resolvedSurface);
    }
    // Run only on mount — resolvedSurface at mount time is surface_min (store empty).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  Wishlist helpers
  const { activeListId, lists, removeFromList, updateQuantity: updateWishlistQuantity } = useWishlist();
  const activeList = lists?.find((list) => list.id === activeListId);
  const wishlistItem = activeList?.products?.find((p) => p.productId === productId);

  // Cart state
  // cartItem always matches the CURRENTLY SELECTED variant (not just any variant).
  // If the user has product in cart with a different surface, isInCart = false — correct.
  const cartItem = cart?.items?.find((item) => item.variant_id === selectedVariant?.id);
  const productPrice = medusaProduct?.variants?.[0].calculated_price?.calculated_amount || 0;
  const productConditioning = resolveProductConditioning(
    medusaProduct?.metadata?.conditioning,
    medusaProduct?.metadata?.conditioning_bac
  );
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);

  const isInCart = Boolean(
    cartItem && Array.isArray(cartItem.metadata?.wishlist_ids) && cartItem.metadata.wishlist_ids.includes(activeListId)
  );

  //  Quantity
  const [count, setCount] = useState(() =>
    cartItem?.quantity
      ? cartItem.quantity
      : normalizePieceQuantity(initialQuantity || quantityStep, productConditioning)
  );

  useEffect(() => {
    const normalizedInitialQuantity = normalizePieceQuantity(
      initialQuantity || quantityStep,
      productConditioning
    );
    if (!isInCart && normalizedInitialQuantity !== count) {
      setCount(normalizedInitialQuantity);
    }
  }, [count, initialQuantity, isInCart, productConditioning, quantityStep]);

  useEffect(() => {
    onQuantityChange?.(count);
  }, [count, onQuantityChange]);

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  const { updateQuantity, isLoading: isUpdatingCart } = useUpdateCartItem({
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const { removeItem } = useRemoveCartItem({
    onSuccess: () => toast.success({ title: __("Item removed from cart!", locale) }),
    onError: (error) => {
      console.error("Error removing item from cart:", error);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Update cart quantity when product IS already in cart from this wishlist */
  const handleQuantityChange = (newQuantity: number) => {
    if (cartItem?.id && isInCart && newQuantity !== cartItem.quantity) {
      updateQuantity(cartItem.id, newQuantity, {
        wishlist_ids: (cartItem.metadata?.wishlist_ids as string[]) || [activeListId],
      });
    }
  };

  /** Update the wishlist quantity (not cart) when product is NOT yet in cart */
  const handleWishlistQuantityChange = (newQuantity: number) => {
    if (wishlistItem?.id && activeListId) {
      updateWishlistQuantity(activeListId, wishlistItem.id, newQuantity);
    }
  };

  const handleDelete = () => {
    const originalWishlistQuantity = wishlistItem?.quantity || 1;

    if (isInCart && cartItem?.id) {
      const productInOtherWishlists = lists?.some(
        (list) => list.id !== activeListId && list.products?.some((p) => p.productId === productId)
      );

      if (productInOtherWishlists) {
        // Product exists in other lists — restore original wishlist quantity
        if (wishlistItem?.id && activeListId) {
          updateWishlistQuantity(activeListId, wishlistItem.id, originalWishlistQuantity);
          setCount(originalWishlistQuantity);
        }
      } else {
        // Only in this list — sync wishlist quantity to cart quantity before removing
        if (wishlistItem?.id && activeListId) {
          updateWishlistQuantity(activeListId, wishlistItem.id, cartItem.quantity || 1);
          setCount(cartItem.quantity);
        }
      }
      removeItem(cartItem.id);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isRange && !selectedVariant) {
      toast.error({ title: __("Please customize this product before adding to cart.", locale) });
      return;
    }

    if (!selectedVariant) return;

    if (hasMultipleVariants) {
      const allVariantIds = medusaProduct?.variants?.map((v) => v.id) ?? [];

      // Correct variant already in cart — just bump quantity + merge wishlist id
      const existingSelectedItem = cart?.items?.find((i) => i.variant_id === selectedVariant.id);
      if (existingSelectedItem) {
        const existing = Array.isArray(existingSelectedItem.metadata?.wishlist_ids)
          ? (existingSelectedItem.metadata.wishlist_ids as string[])
          : [];
        const merged = activeListId ? Array.from(new Set([...existing, activeListId])) : existing;
        updateQuantity(existingSelectedItem.id, existingSelectedItem.quantity + count, {
          wishlist_ids: merged,
        });
        toast.success({ title: __("Item added to cart!", locale) });
        return;
      }

      // Different variant in cart — remove it first (surface swap)
      const existingOtherItem = cart?.items?.find(
        (i) => i.variant_id !== selectedVariant.id && allVariantIds.includes(i.variant_id ?? "")
      );
      if (existingOtherItem) removeItem(existingOtherItem.id);

      addToCart(selectedVariant.id, count, {
        wishlist_ids: activeListId ? [activeListId] : [],
      });
      return;
    }

    // Single-variant path
    const variantId = selectedVariant.id;
    const existingCartItem = cart?.items?.find((i) => i.variant_id === variantId);

    if (existingCartItem && !isInCart) {
      // Product already in cart from a different wishlist — merge wishlist id
      const existing = Array.isArray(existingCartItem.metadata?.wishlist_ids)
        ? existingCartItem.metadata.wishlist_ids
        : [];
      const merged = existing.includes(activeListId ?? "") ? existing : [...existing, activeListId];
      updateQuantity(existingCartItem.id, existingCartItem.quantity + count, {
        wishlist_ids: merged,
      });
      toast.success({ title: __("Item added to cart!", locale) });
    } else {
      addToCart(variantId, count, {
        wishlist_ids: [activeListId || ""],
      });
    }
  };

  // ── Display helpers ───────────────────────────────────────────────────────
  const pricePerPiece = toPiecePrice(productPrice, productConditioning);
  const isFullImage = product?.data?.image_full_size_card;
  const isPriceDynamic = medusaProduct?.metadata?.is_price_dynamic as boolean;
  const value = formatPrice(toLotPrice(productPrice, productConditioning));
  // For the "in cart" price badge (shows what was actually paid)
  const selectedVariantPriceFromCart = formatPrice(cartItem?.unit_price || 0, { showDecimals: true });

  return (
    <>
      {/* ── Desktop ─────────────────────────────────────────────────────── */}
      <li className="flex items-center gap-5.5 max-md:hidden">
        <figure className="bg-product-card relative flex h-[110px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-md">
          {isFullImage ? (
            <Image
              className="absolute object-cover mix-blend-multiply"
              src={
                sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                product.data.images[0]?.image.url ||
                product.data.featuredImage.url ||
                ""
              }
              alt={product?.data?.title ?? ""}
              fill
            />
          ) : (
            <div className="relative size-20">
              <Image
                className="absolute object-contain mix-blend-multiply"
                src={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  product.data.images[0]?.image.url ||
                  product.data.featuredImage.url ||
                  ""
                }
                alt={product?.data?.title ?? ""}
                fill
              />
            </div>
          )}
        </figure>

        {/* ── Range / dynamic: check BEFORE isInCart so the modal always sits
             at the same tree position. If we checked isInCart first, React
             would unmount the IN-CART branch and mount the RANGE branch
             (or vice-versa) the moment isInCart flips — killing the modal's
             open state mid-interaction (the first-change auto-close bug). ── */}
        {isRange || isPriceDynamic ? (
          <dl className="flex w-full items-center justify-between gap-3">
            <div className="space-y-2">
              <div className="flex flex-col gap-1">
                <h3 className="font-cabinet-grotesk text-2xl font-bold">
                  {product?.data?.title || medusaProduct?.title}
                </h3>
                <div className="flex items-center gap-2">
                  {isSaleProduct(medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
                  {/* NOT-IN-CART variant price badge */}
                  {!isInCart && count > 1 && hasMultipleVariants && (
                    <ProductBadge
                      text={_x("Price : {{ selectedVariantPrice }}", { selectedVariantPrice }, locale)}
                      className="w-fit text-sm font-normal text-inherit"
                    />
                  )}
                </div>
              </div>

              {/* Modal at a stable tree position — never inside a branch that
                  depends on isInCart, so React preserves the instance (and its
                  open state) when the cart state changes. */}
              {isRange && (
                <ProductConfigurationModal
                  addProductToCart={isInCart}
                  products={[product]}
                  medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                  locale={locale}
                  isInWishlist={isInCart}
                  activeListId={isInCart ? activeListId : undefined}
                />
              )}

              {/* IN-CART price badge */}
              {isInCart && count > 1 && hasMultipleVariants && (
                <ProductBadge
                  text={_x("Price : {{ selectedVariantPriceFromCart }}", { selectedVariantPriceFromCart }, locale)}
                  className="w-fit text-sm font-normal text-inherit"
                />
              )}
            </div>

            {/* Right side — price and controls differ by isInCart */}
            <div className={cn("flex flex-col items-end gap-1", isRange && "gap-2")}>
              {isInCart ? (
                <>
                  {hasMultipleVariants ? (
                    <h3 className="font-cabinet-grotesk text-2xl font-bold">
                      {formatPrice((cartItem?.unit_price || 0) * count, { showDecimals: true })}
                    </h3>
                  ) : (
                    <PriceToastDropdown locale={locale} />
                  )}
                  <div className="flex items-center gap-3.5">
                    <ConfirmDeleteModal
                      modalTrigger={
                        <button className="group cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out">
                          <Trash2Icon size={20} className="group-hover:text-pink-primary text-primary-light" />
                        </button>
                      }
                      title={__("Delete item", locale)}
                      text={__("Are you sure you want to remove this item from your cart?", locale)}
                      onClick={handleDelete}
                      locale={locale}
                    />
                    <QuantitySelector
                      locale={locale}
                      variant="secondary"
                      className="bg-white"
                      count={count}
                      setCount={setCount}
                      onQuantityChange={handleQuantityChange}
                      disabled={isUpdatingCart}
                      step={quantityStep}
                    />
                  </div>
                </>
              ) : (
                medusaProduct?.status !== "draft" && (
                  <div className="flex items-center gap-2">
                    <ConfirmDeleteModal
                      modalTrigger={
                        <button className="group mt-8 cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out">
                          <Trash2Icon size={20} className="group-hover:text-pink-primary text-primary-light" />
                        </button>
                      }
                      title={__("Delete item", locale)}
                      text={__("Are you sure you want to remove this product from your wishlist?", locale)}
                      onClick={() => removeFromList(activeListId || "", productId)}
                      locale={locale}
                    />
                    <div className="flex flex-col items-end gap-2">
                      {hasMultipleVariants ? (
                        <h3 className="font-cabinet-grotesk text-2xl font-bold">
                          {formatPrice((selectedVariant?.calculated_price?.calculated_amount || 0) * count, {
                            showDecimals: true,
                          })}
                        </h3>
                      ) : (
                        <PriceToastDropdown locale={locale} />
                      )}
                      <QuantitySelector
                        locale={locale}
                        variant="default"
                        className="bg-white"
                        count={count}
                        setCount={setCount}
                        onQuantityChange={handleWishlistQuantityChange}
                        step={quantityStep}
                      />
                      <Button
                        variant="primary"
                        disabled={isAddingToCart}
                        onClick={handleAddToCart}
                        className="h-10 w-[150px] font-bold"
                        size="sm"
                      >
                        {isAddingToCart ? <Loader2 className="animate-spin" /> : __("Add to cart", locale)}
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          </dl>
        ) : /* ── Standard product (no range/dynamic) — IN CART ───────────── */
        isInCart ? (
          <dl className="flex w-full items-center justify-between">
            <div className="space-y-2">
              <div className="flex flex-col gap-1">
                <h3 className="font-cabinet-grotesk text-2xl font-bold">
                  {product?.data?.title || medusaProduct?.title}
                </h3>
                {isSaleProduct(medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
              </div>

              {count > 1 && (
                <ProductBadge
                  text={_x("Price : {{ value }}", { value }, locale)}
                  className="w-fit text-sm font-normal text-inherit"
                />
              )}
              <div className="flex items-center gap-2">
                {physicalPieceCount > 1 && (
                  <dd className="text-primary-light text-sm capitalize">
                    {formatPrice(pricePerPiece, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/
                    {__("piece", locale)}
                  </dd>
                )}
                {physicalPieceCount > 1 && (
                  <ProductBadge className="max-w-full whitespace-normal text-right" text={formatProductQuantity(count, productConditioning, locale)} />
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <h3 className="font-cabinet-grotesk text-2xl font-bold">{formatPrice(productPrice * count)}</h3>
              <div className="flex items-center gap-3.5">
                <ConfirmDeleteModal
                  modalTrigger={
                    <button className="group cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out">
                      <Trash2Icon size={20} className="group-hover:text-pink-primary text-primary-light" />
                    </button>
                  }
                  title={__("Delete item", locale)}
                  text={__("Are you sure you want to remove this product from your wishlist?", locale)}
                  onClick={handleDelete}
                  locale={locale}
                />
                <QuantitySelector
                  locale={locale}
                  variant="secondary"
                  className="bg-white"
                  count={count}
                  setCount={setCount}
                  onQuantityChange={handleQuantityChange}
                  disabled={isUpdatingCart}
                  step={quantityStep}
                />
              </div>
            </div>
          </dl>
        ) : (
          /* ── Standard product — NOT IN CART ──────────────────────────── */
          <dl className="flex w-full items-center justify-between gap-3">
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h3 className="font-cabinet-grotesk text-2xl font-bold">
                    {product?.data?.title || medusaProduct?.title}
                  </h3>
                  {isSaleProduct(medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
                </div>
              </div>

              {medusaProduct?.status === "draft" && (
                <Toast
                  id={`wishlist-${medusaProduct?.id}`}
                  type="info"
                  className="min-[384px]:min-w-none mb-0.5 w-fit max-w-none py-3 shadow-none max-sm:p-2"
                  titleClassName="max-sm:text-[10px]"
                  title={__("This product is out of stock", locale)}
                />
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {count > 1 && (
                    <ProductBadge
                      text={_x("Price : {{ value }}", { value }, locale)}
                      className="w-fit text-sm font-normal text-inherit"
                    />
                  )}
                  <dt className="sr-only">Price per piece</dt>
                  {physicalPieceCount > 1 && (
                    <dd className="text-primary-light text-sm capitalize">
                      {formatPrice(pricePerPiece, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/
                      {__("piece", locale)}
                    </dd>
                  )}
                  {physicalPieceCount > 1 && (
                    <ProductBadge className="max-w-full whitespace-normal text-right" text={formatProductQuantity(count, productConditioning, locale)} />
                  )}
                </div>
                <ConfirmDeleteModal
                  modalTrigger={
                    <button className="group cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out">
                      <Trash2Icon size={20} className="group-hover:text-pink-primary text-primary-light" />
                    </button>
                  }
                  title={__("Delete item", locale)}
                  text={__("Are you sure you want to remove this product from your wishlist?", locale)}
                  onClick={() => removeFromList(activeListId || "", productId)}
                  locale={locale}
                />
              </div>
            </div>

            {medusaProduct?.status !== "draft" && (
              <div className="flex flex-col items-end gap-2">
                <h3 className="font-cabinet-grotesk text-2xl font-bold">{formatPrice(productPrice * count)}</h3>
                <QuantitySelector
                  locale={locale}
                  variant="default"
                  className="bg-white"
                  count={count}
                  setCount={setCount}
                  onQuantityChange={handleWishlistQuantityChange}
                  step={quantityStep}
                />
                <Button
                  variant="primary"
                  disabled={isAddingToCart || isUpdatingCart}
                  onClick={handleAddToCart}
                  className="h-10 w-[150px] font-bold"
                  size="sm"
                >
                  {isAddingToCart || isUpdatingCart ? <Loader2 className="animate-spin" /> : __("Add to cart", locale)}
                </Button>
              </div>
            )}
          </dl>
        )}
      </li>

      {/* ── Mobile: delegate to CartProductCard ─────────────────────────── */}
      <li className="md:hidden">
        <CartProductCard
          // Pass the RESOLVED variant id, not always variants[0].
          // For multi-variant products this ensures the mobile card works
          // with the same surface that was selected on desktop.
          medusaVariantId={selectedVariant?.id || medusaProduct?.variants?.[0].id || ""}
          productImage={
            sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
            product?.data?.images?.[0]?.image?.url ||
            product?.data?.featuredImage?.url ||
            ""
          }
          productName={product.data.title || medusaProduct?.title || ""}
          alt={product?.data?.images?.[0]?.image?.alt || product.data.title || ""}
          productPrice={getMedusaProductPrice(medusaProduct)}
          onQuantityChange={setCount}
          locale={locale}
          productConditioning={productConditioning}
          price={productPrice}
          productId={productId}
          variant="wishlist"
          isFullImage={isFullImage}
          isOutOfStock={medusaProduct?.status === "draft"}
          isRange={isRange}
          prismicProduct={product}
          medusaProduct={medusaProduct}
          isPriceDynamic={isPriceDynamic}
        />
      </li>
    </>
  );
};

export default WishlistProductCard;
